import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'

export const fetchRootCategories = createAsyncThunk(
  'categories/fetchRoots',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/categories/roots')
      return data // array of nodes
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message)
    }
  }
)

export const fetchChildren = createAsyncThunk(
  'categories/fetchChildren',
  async (parentId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/categories/${parentId}/children`)
      return { parentId, children: data }
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message)
    }
  }
)

// Admin thunks
export const createCategoryAdmin = createAsyncThunk(
  'categories/createCategoryAdmin',
  async ({ name, parentId }, { rejectWithValue, getState }) => {
    try {
      const token = getState()?.user?.userInfo?.token
      const { data } = await axios.post('/api/categories', { name, parentId }, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return data
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message)
    }
  }
)

export const updateCategoryAdmin = createAsyncThunk(
  'categories/updateCategoryAdmin',
  async ({ id, name }, { rejectWithValue, getState }) => {
    try {
      const token = getState()?.user?.userInfo?.token
      const { data } = await axios.put(`/api/categories/${id}`, { name }, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return data
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message)
    }
  }
)

export const deleteCategoryAdmin = createAsyncThunk(
  'categories/deleteCategoryAdmin',
  async ({ id }, { rejectWithValue, getState }) => {
    try {
      const token = getState()?.user?.userInfo?.token
      await axios.delete(`/api/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return { id }
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message)
    }
  }
)

const initialState = {
  byId: {}, // id -> node
  childrenByParent: {}, // parentId (or 'null') -> [childIds]
  roots: [],
  loading: false,
  error: null,
  lastMutationError: null,
}

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRootCategories.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRootCategories.fulfilled, (state, action) => {
        state.loading = false
        const nodes = Array.isArray(action.payload) ? action.payload : []
        const ids = []
        nodes.forEach((n) => {
          if (n && n._id) {
            state.byId[n._id] = { ...state.byId[n._id], ...n }
            ids.push(n._id)
          }
        })
        state.roots = ids
        state.childrenByParent['null'] = ids
      })
      .addCase(fetchRootCategories.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to load root categories'
      })
      .addCase(fetchChildren.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchChildren.fulfilled, (state, action) => {
        state.loading = false
        const parentId = action.payload?.parentId
        const nodes = Array.isArray(action.payload?.children) ? action.payload.children : []
        const ids = []
        nodes.forEach((n) => {
          if (n && n._id) {
            state.byId[n._id] = { ...state.byId[n._id], ...n, parent: parentId }
            ids.push(n._id)
          }
        })
        if (parentId != null) state.childrenByParent[String(parentId)] = ids
      })
      .addCase(fetchChildren.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to load categories'
      })
      // Create
      .addCase(createCategoryAdmin.pending, (state) => {
        state.lastMutationError = null
      })
      .addCase(createCategoryAdmin.fulfilled, (state, action) => {
        const n = action.payload
        if (n && n._id) {
          state.byId[n._id] = { ...state.byId[n._id], ...n }
          // Insert under parent mapping if available
          const key = n.parent == null ? 'null' : String(n.parent)
          const list = state.childrenByParent[key] || []
          state.childrenByParent[key] = [...list, n._id]
          if (key === 'null') {
            state.roots = state.childrenByParent['null']
          }
        }
      })
      .addCase(createCategoryAdmin.rejected, (state, action) => {
        state.lastMutationError = action.payload || 'Create category failed'
      })
      // Update
      .addCase(updateCategoryAdmin.fulfilled, (state, action) => {
        const n = action.payload
        if (n && n._id) {
          state.byId[n._id] = { ...state.byId[n._id], ...n }
        }
      })
      .addCase(updateCategoryAdmin.rejected, (state, action) => {
        state.lastMutationError = action.payload || 'Update category failed'
      })
      // Delete
      .addCase(deleteCategoryAdmin.fulfilled, (state, action) => {
        const id = action.payload?.id
        if (!id) return
        // Remove from byId
        delete state.byId[id]
        // Remove from parent's children list
        Object.keys(state.childrenByParent).forEach((k) => {
          state.childrenByParent[k] = (state.childrenByParent[k] || []).filter((cid) => cid !== id)
        })
        // Refresh roots cache
        state.roots = state.childrenByParent['null'] || []
      })
      .addCase(deleteCategoryAdmin.rejected, (state, action) => {
        state.lastMutationError = action.payload || 'Delete category failed'
      })
  },
})

export default categoriesSlice.reducer
